import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { AccountService } from '@wilson/account';
import { OrganizationalUnit, TimelineShift, User } from '@wilson/interfaces';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  TimelineRemoveShiftFromUserV2,
  TimelineUpsertNewShiftV2,
} from '@wilson/shift-timeline/state';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { TimelineShiftAssignService } from '../../services/timeline-shift-assign.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';
import {
  AssignAllGroupItemsParams,
  GroupAssignmentHelperService,
} from '../group-assignment-confirmation-modal/group-assignment-helper.service';
import { GroupAssignmentConfirmationModalResult } from '../group-assignment-confirmation-modal/group-assignment-confirmation-modal.component';
import { ShiftAccessRightConfirmationModalResult } from '../shift-access-right-confirmation-modal/shift-access-right-confirmation-modal.component';

interface AssignShiftDropView {
  assignAllGroupItems: (params: AssignAllGroupItemsParams) => Promise<void>;
  assignSingleShift: (
    userId: string,
    draggingShift: TimelineShift,
  ) => Promise<void>;
  /** When true, assign errors propagate to the shared drop handler catch block. */
  rethrowAssignErrors: boolean;
}

interface AssignSingleShiftStrategy {
  assign: (userId: string, shift: TimelineShift) => Promise<void>;
  onBeforeAssign?: (shift: TimelineShift) => void;
  onAssignSuccess?: () => void;
  onAssignError?: (error: unknown, shift: TimelineShift) => void;
  onFinally?: (shift: TimelineShift) => void;
}

@Injectable()
export class AssignShiftDropZoneHelperService {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineShiftAssignService = inject(
    TimelineShiftAssignService,
  );
  private readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  private readonly accountService = inject(AccountService);
  private readonly groupAssignmentHelperService = inject(
    GroupAssignmentHelperService,
  );

  private readonly standardAssignDropView: AssignShiftDropView = {
    assignAllGroupItems: (params) =>
      this.groupAssignmentHelperService.assignAllGroupItemsInStandardView(
        params,
      ),
    assignSingleShift: (userId, draggingShift) =>
      this.assignSingleShiftInStandardView(userId, draggingShift),
    rethrowAssignErrors: false,
  };

  private readonly cellAssignDropView: AssignShiftDropView = {
    assignAllGroupItems: (params) =>
      this.groupAssignmentHelperService.assignAllGroupItemsInCellView(params),
    assignSingleShift: (userId, draggingShift) =>
      this.assignSingleShiftInCellView(userId, draggingShift),
    rethrowAssignErrors: true,
  };

  public async onDragOverHandler(userId: string | undefined): Promise<void> {
    if (userId) {
      const draggingShift = await firstValueFrom(
        this.dragAndDropModeService.draggingShiftV2$,
      );

      if (draggingShift) {
        this.dragAndDropModeService.setDraggingItemId(
          CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        );
        this.store.dispatch(
          new TimelineUpsertNewShiftV2({
            shift: {
              ...draggingShift,
              id: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
              userId: userId,
            },
          }),
        );
      }
    }
  }

  public async onDragLeaveHandler(userId: string | undefined): Promise<void> {
    if (userId) {
      const draggingShift = await firstValueFrom(
        this.dragAndDropModeService.draggingShiftV2$,
      );
      if (draggingShift?.id) {
        this.removeDraggingShift(userId);
      }
    }
  }

  public removeDraggingShift(userId: string): void {
    this.dragAndDropModeService.setDraggingItemId(null);
    this.store.dispatch(
      new TimelineRemoveShiftFromUserV2({
        userId: userId,
        shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
      }),
    );
  }

  public async onDropStandardViewHandler(
    userId: string | undefined,
    draggingShift: TimelineShift | null,
  ): Promise<void> {
    await this.handleAssignDrop(
      userId,
      draggingShift,
      this.standardAssignDropView,
    );
    this.timelineShiftDragAndDropDirectiveHelperService.handleDragEnd(false);
  }

  public async onDropCellViewHandler(
    userId: string | undefined,
    draggingShift: TimelineShift | null,
  ): Promise<void> {
    await this.handleAssignDrop(userId, draggingShift, this.cellAssignDropView);
    this.timelineShiftDragAndDropDirectiveHelperService.handleCellViewDragEnd();
  }

  private async handleAssignDrop(
    userId: string | undefined,
    draggingShift: TimelineShift | null,
    view: AssignShiftDropView,
  ): Promise<void> {
    const userConfirmedPublishedShiftEdit = draggingShift
      ? await this.timelineCommonOperationsService.checkAndConfirmPublishedShiftDrop(
          draggingShift.publicationStatus,
        )
      : false;

    if (userId && draggingShift?.id) {
      if (userConfirmedPublishedShiftEdit) {
        try {
          const { decision, groupItems } =
            await this.groupAssignmentHelperService.checkAndConfirmGroupAssignment(
              draggingShift.id,
            );

          if (decision) {
            if (
              decision === GroupAssignmentConfirmationModalResult.MoveAllItems
            ) {
              await view.assignAllGroupItems({
                userId,
                draggingShift,
                groupItems,
              });
            } else {
              await view.assignSingleShift(userId, draggingShift);
            }
          } else {
            this.cancelDraggingShift(userId);
          }
        } catch (error) {
          if (view.rethrowAssignErrors) {
            this.cancelDraggingShift(userId);
            this.nzMessageService.error(
              this.translate.instant('general.something_went_wrong'),
            );
          } else {
            captureException(error);
            this.nzMessageService.error(
              this.translate.instant('general.something_went_wrong'),
            );
            this.cancelDraggingShift(userId);
          }
        }
      } else {
        this.cancelDraggingShift(userId);
      }
    }
  }

  private async assignSingleShiftInStandardView(
    userId: string,
    draggingShift: TimelineShift,
  ): Promise<void> {
    try {
      await this.assignSingleShiftWithStrategy(userId, draggingShift, {
        assign: (targetUserId, shift) =>
          this.timelineShiftAssignService.handleStandardViewShiftsWithoutActivitiesAssignments(
            targetUserId,
            [shift],
          ),
        onBeforeAssign: (shift) =>
          this.createNewViaDragAndDropService.addUpdatingId(shift.id),
        onAssignSuccess: () => {
          this.dragAndDropModeService.setDraggingShiftV2(null);
          this.dragAndDropModeService.setDraggingItemId(null);
          this.store.dispatch(new SetDemarcatorRange([]));
        },
        onAssignError: () => {
          this.store.dispatch(
            new TimelineRemoveShiftFromUserV2({
              userId,
              shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            }),
          );
          this.dragAndDropModeService.setDraggingShiftV2(null);
          this.nzMessageService.error(
            this.translate.instant('general.something_went_wrong'),
          );
        },
        onFinally: (shift) =>
          this.createNewViaDragAndDropService.removeUpdatingId(shift.id),
      });
    } catch (error) {
      captureException(error);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.cancelDraggingShift(userId);
    }
  }

  private async assignSingleShiftInCellView(
    userId: string,
    draggingShift: TimelineShift,
  ): Promise<void> {
    await this.assignSingleShiftWithStrategy(userId, draggingShift, {
      assign: (targetUserId, shift) =>
        this.timelineShiftAssignService.handleCellViewShiftsWithoutActivitiesAssignments(
          targetUserId,
          [shift],
        ),
      onAssignSuccess: () => {
        this.dragAndDropModeService.setDraggingShiftV2(null);
        this.dragAndDropModeService.setDraggingItemId(null);
      },
    });
  }

  private async assignSingleShiftWithStrategy(
    userId: string,
    draggingShift: TimelineShift,
    strategy: AssignSingleShiftStrategy,
  ): Promise<void> {
    const user = (await firstValueFrom(
      this.accountService.get(userId, {
        relations: ['accessibleOrganizationalUnits'],
      }),
    )) as User & {
      accessibleOrganizationalUnits: OrganizationalUnit[];
    };

    const shiftAccessRightConfirmationModalResult =
      await this.timelineCommonOperationsService.checkAndConfirmDifferentOrganizationalUnit(
        draggingShift,
        user,
      );

    if (shiftAccessRightConfirmationModalResult) {
      const shiftToUpdate =
        shiftAccessRightConfirmationModalResult ===
        ShiftAccessRightConfirmationModalResult.UpdateShift
          ? {
              ...draggingShift,
              organizationalUnitId: user.organizationalUnitId,
            }
          : { ...draggingShift };

      this.store.dispatch(
        new TimelineRemoveShiftFromUserV2({
          userId,
          shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        }),
      );

      strategy.onBeforeAssign?.(shiftToUpdate);

      try {
        await strategy.assign(userId, shiftToUpdate);
        strategy.onAssignSuccess?.();
      } catch (error) {
        if (strategy.onAssignError) {
          strategy.onAssignError(error, shiftToUpdate);
        } else {
          throw error;
        }
      } finally {
        strategy.onFinally?.(shiftToUpdate);
      }
    } else {
      this.cancelDraggingShift(userId);
    }
  }

  private cancelDraggingShift(userId: string): void {
    this.store.dispatch(
      new TimelineRemoveShiftFromUserV2({
        userId: userId,
        shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
      }),
    );
    this.dragAndDropModeService.setDraggingShiftV2(null);
    this.dragAndDropModeService.setDraggingItemId(null);
    this.store.dispatch(new SetDemarcatorRange([]));
  }
}
